import React, { useMemo } from 'react';
import { Form, Textarea, Button } from '@crisellevaldez/speculo-ui';
import { Info, Undo2 } from 'lucide-react';
import { Flow } from '../../../../hooks/useFlowsData';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUser } from '../../../../hooks/useUser';
import { getDayOfWeek, getPartOfDay, getWeekOrWeekend } from '../../../../utils/voicemail';
import { Tooltip } from '../../../common/Tooltip';
import { RegenerateVoicemailButton } from '../RegenerateVoicemailButton';

interface FlowVoicemailProps {
  flow: Flow;
  voicemailContent: string;
  onVoicemailContentChange: (content: string) => void;
  disabled?: boolean;
}

export const FlowVoicemail: React.FC<FlowVoicemailProps> = ({
  flow,
  voicemailContent,
  onVoicemailContentChange,
  disabled,
}) => {
  const { organization } = useOrganizationStore();
  const { user } = useUser();

  // Get dynamic values for the voicemail preview
  const previewValues = useMemo(() => {
    const firstName = user?.displayName?.split(' ')[0] || 'John';
    const assistantName = organization?.assistant_name || 'Grace';
    const companyName = organization?.org_name || 'ABC Realty';
    const dayOfWeek = getDayOfWeek();
    const partOfDay = getPartOfDay();
    const weekOrWeekend = getWeekOrWeekend();

    return { firstName, assistantName, companyName, dayOfWeek, partOfDay, weekOrWeekend };
  }, [user?.displayName, organization?.assistant_name, organization?.org_name]);

  return (
    <div data-tour="edit-flow-voicemail">
      <Form.Control>
        <div className="flex items-center gap-3 mb-2">
          <Form.Label className="mb-0">Voicemail Message</Form.Label>
          <Tooltip
            content="Edit the body directly or click 'Regenerate Voicemail' to create a new voicemail based on your flow description. The intro and outro are automatically personalized for each contact."
            position="right"
          >
            <span className="group rounded-full transition-colors cursor-help flex items-center">
              <Info className="w-4 h-4 text-muted-foreground group-hover:text-yellow-500" />
            </span>
          </Tooltip>
          <RegenerateVoicemailButton
            flowId={flow?.id || ''}
            flowDescription={flow?.description || ''}
            onRegenerated={(voicemail) => { if (voicemail) onVoicemailContentChange(voicemail); }}
            disabled={disabled}
          />
          {voicemailContent !== (flow?.voicemailContent || '') && !disabled && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 h-8 px-2"
              onClick={() => onVoicemailContentChange(flow?.voicemailContent || '')}
              title="Revert to saved voicemail"
            >
              <Undo2 className="w-3.5 h-3.5" />
              Revert
            </Button>
          )}
        </div>

        {/* Voicemail Structure Container */}
        <div className="border border-gray-300 dark:border-white/10 rounded-lg overflow-hidden shadow-sm">
          {/* Intro Section - Auto-generated */}
          <div className="bg-yellow-50/80 dark:bg-yellow-900/20 px-4 py-3 border-b border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider">
                Intro (Auto-personalized)
              </span>
            </div>
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
              "Hey there{' '}
              <span className="font-semibold text-yellow-900 dark:text-yellow-300">{previewValues.firstName}</span>, This
              is{' '}
              <span className="font-semibold text-yellow-900 dark:text-yellow-300">{previewValues.assistantName}</span>{' '}
              with{' '}
              <span className="font-semibold text-yellow-900 dark:text-yellow-300">{previewValues.companyName}</span>. I
              hope you are having a great{' '}
              <span className="font-semibold text-yellow-900 dark:text-yellow-300">
                {previewValues.dayOfWeek} {previewValues.partOfDay}
              </span>
              ."
            </p>
          </div>

          {/* Body Section - User's Voicemail Content (Editable) */}
          <div className="bg-white dark:bg-white/5 border-l-4 border-l-yellow-400">
            <div className="px-4 pt-3 pb-1 flex items-center gap-2">
              <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider">
                Body (Your Message)
              </span>
              <span className="text-sm text-muted-foreground">— Click to edit</span>
            </div>
            <div className="px-2 pb-2">
              <Textarea
                value={voicemailContent || ''}
                onChange={(e) => onVoicemailContentChange(e.target.value)}
                rows={5}
                className="border-0 text-sm"
                placeholder="Write your voicemail message here. Example: I was hoping to catch you for a quick chat. Please give us a call when you get a chance."
                disabled={disabled}
              />
            </div>
          </div>

          {/* Outro Section - Auto-generated */}
          <div className="bg-yellow-50/80 dark:bg-yellow-900/20 px-4 py-3 border-t border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider">
                Outro (Auto-personalized)
              </span>
            </div>
            <p className="text-sm text-geay-800 dark:text-gray-200 leading-relaxed">
              "Thanks,{' '}
              <span className="font-semibold text-yellow-900 dark:text-yellow-300">{previewValues.firstName}</span>. Have
              an amazing rest of your{' '}
              <span className="font-semibold text-yellow-900 dark:text-yellow-300">{previewValues.weekOrWeekend}</span>!"
            </p>
          </div>
        </div>
      </Form.Control>
    </div>
  );
};
